import { Checkbox, Tooltip } from 'antd';
import { t } from 'i18next';
import moment from 'moment';
import React, { ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/redux/hook';

import { BiLabel } from 'react-icons/bi';
import NotificationThreeDot from 'src/app/pages/NotificationsPage/Dropdowns/NotificationThreeDot';
import styles from 'src/app/pages/NotificationsPage/NotificationsPage.module.scss';
import { convertShowSettingsLabel } from 'src/app/pages/SettingsPage/NotificationSettingsMain/NotificationSettings/notification-settings.config';
import {
  checkNotification,
  resetCheckedNotifications,
  setSelectedNotification,
  uncheckNotification,
  updateNotifications,
} from 'src/app/redux/notifications/notificationsSlice';
import { NotificationResultType } from 'src/app/services/notifications/notifications-service.type';
import { NotificationLabelPriorityStatus } from 'src/enums/labels.enum';
import { NotificationReadStatusEnum, NotificationStarStatusEnum } from 'src/enums/notifications.enum';
import LabelImportantIcon from 'src/icons/LabelImportantIcon';
import StarFillIcon from 'src/icons/StarFillIcon';
import StarIcon from 'src/icons/StarIcon';
import { LabelType } from 'src/types/labels.type';

interface INotificationContentProps {
  item: NotificationResultType;
  isStatic?: boolean;
  labels: LabelType[];
}

const NotificationWithLabel = ({
  color,
  title,
  status,
}: {
  color: string;
  title: string;
  status: NotificationLabelPriorityStatus;
}) => {
  return (
    <Tooltip title={title}>
      <div className={styles.iconContainer}>
        {status === NotificationLabelPriorityStatus.Uncategorized ? (
          <BiLabel color="color" size={22} />
        ) : (
          <LabelImportantIcon
            className={styles.labelImportantIcon}
            color={color}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    </Tooltip>
  );
};

export const NotificationContent: React.FC<INotificationContentProps> = ({ item, isStatic, labels }) => {
  const dispatch = useAppDispatch();

  const { checkedNotifications, selectedNotification } = useAppSelector((state) => state.notificationsState);

  const { userLabelShow, systemLabelShow } = useAppSelector((state) => state.user);

  const onRowClick = async (value: NotificationResultType) => {
    if (value.readStatus === NotificationReadStatusEnum.Unread) {
      await dispatch(
        updateNotifications({
          ids: [value.id],
          readStatus: NotificationReadStatusEnum.Read,
        }),
      ).unwrap();
    }
    dispatch(setSelectedNotification(value));
    dispatch(resetCheckedNotifications());
    dispatch(checkNotification(value.id));
  };

  const getIconColorAndTextWithPriorityNumber = (notificationLabels: LabelType[]): ReactNode => {
    let minNumber: NotificationLabelPriorityStatus = NotificationLabelPriorityStatus.Uncategorized;

    notificationLabels?.forEach((obj) => {
      const label = labels.find((lbl) => lbl.id === obj.id);
      if (!label) return;
      if (label.priority < minNumber) minNumber = label.priority;
    });

    switch (Number(minNumber)) {
      case NotificationLabelPriorityStatus.Critical:
        return <NotificationWithLabel color="red" title="High" status={minNumber} />;
      case NotificationLabelPriorityStatus.Low:
        return <NotificationWithLabel color="yellow" title="Low" status={minNumber} />;

      case NotificationLabelPriorityStatus.Medium:
        return <NotificationWithLabel color="#92aabb" title="Medium" status={minNumber} />;

      case NotificationLabelPriorityStatus.Uncategorized:
        return <NotificationWithLabel color="#92aabb" title="Uncategorized" status={minNumber} />;
      default:
        return (
          <NotificationWithLabel
            color="#92aabb"
            title="Uncategorized"
            status={NotificationLabelPriorityStatus.Uncategorized}
          />
        );
    }
  };

  return (
    <div
      className={styles.notificationRow}
      style={
        selectedNotification?.id === item.id && !isStatic
          ? { backgroundColor: 'var(--notification_mail_static_bg)' }
          : item.readStatus
            ? { backgroundColor: 'var(--page_content_bg)' }
            : undefined
      }
      onClick={() => onRowClick(item)}
      key={item.id}
    >
      <div className={styles.checkNotification}>
        {!isStatic ? (
          <Checkbox
            checked={checkedNotifications.includes(item.id)}
            onChange={(e) => {
              const { checked } = e.target;
              if (checked) dispatch(checkNotification(item.id));
              else dispatch(uncheckNotification(item.id));
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : null}
        <p className={styles.notificationTime}>{moment(item.createdAt).format('HH:mm')}</p>
      </div>
      <div className={styles.rowIcons}>
        {getIconColorAndTextWithPriorityNumber(item.label)}
        {/* <Tooltip title={getIconColorAndTextWithPriorityNumber(item.label, labels).title}>
          <div className={styles.iconContainer}>
            <LabelImportantIcon
              className={styles.labelImportantIcon}
              color={getIconColorAndTextWithPriorityNumber(item.label, labels).color}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </Tooltip> */}
        <Tooltip
          title={
            item.starStatus === NotificationStarStatusEnum.Star
              ? t('notificationsPage.markUnStarTitle')
              : t('notificationsPage.markStarTitle')
          }
        >
          <div
            className={styles.iconContainer}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                updateNotifications({
                  ids: [item.id],
                  starStatus: Number(!item.starStatus),
                }),
              );
            }}
          >
            {item.starStatus === NotificationStarStatusEnum.Star ? (
              <StarFillIcon color="#ffcd3c" className={styles.starIcon} />
            ) : (
              <StarIcon className={styles.starIcon} />
            )}
          </div>
        </Tooltip>
      </div>
      <div className={styles.notificationContent}>
        <div className={styles.subjectContainer} style={item.readStatus ? { color: 'rgb(128, 128, 128)' } : undefined}>
          <div className={styles.title}>{item.subject}</div>
          <div className={styles.newsTitle}>{item.content}</div>
        </div>

        {convertShowSettingsLabel({
          list: item.label,
          systemLabelShow,
          userLabelShow,
        }).map((obj) => {
          return (
            <Tooltip title={obj.name} key={obj.id}>
              <div className={styles.labelTag} style={{ backgroundColor: obj.backgroundColor }}>
                {obj.name.charAt(0)}
              </div>
            </Tooltip>
          );
        })}
      </div>

      <NotificationThreeDot notification={item} />
    </div>
  );
};
